import { useState, type FormEvent, type JSX } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { buildRoomPath, SPECIES_META, type DuckColor, type DuckSpecies } from '@duck/contracts';
import { api, type ApiError } from '../../lib/api';
import { usePageMeta } from '../../lib/page-meta';
import { useAuthStore } from '../../state/auth.store';
import { AuthShell, ErrorText, Field, SubmitButton } from '../../components/ui';
import { DuckPicker } from '../onboarding/DuckPicker';
import { SpeciesPicker, CritterPreview } from '../onboarding/CharacterPicker';

/**
 * "/join/:key" — frictionless guest entry into a guild's world (Task 4). Anyone who opens the invite
 * link is in with only a NAME + a DUCK — no email, no password. The warm invite preview ("Joining Acme
 * as employee") greets them; role comes from the invite.
 *
 * The invite URL is AUTHORITATIVE about which world you enter (room/session fix, ADR-0019): it always
 * governs, even over a stale session from another guild. If you're already a member of THIS guild we
 * slip you straight in; otherwise the entry form runs and acceptGuestInvite switches your session to
 * the invited world — so a prior session in workspace A can never hijack an invite to workspace B.
 *
 * Returning device: your last name + duck are prefilled, so joining a NEW guild is a single tap.
 */
export function JoinPage(): JSX.Element {
  usePageMeta({ title: 'Join a space — Duck Duck Space', noindex: true });
  const { key = '' } = useParams();
  const navigate = useNavigate();
  const authed = useAuthStore((s) => s.tokens !== null);
  const currentWorkspaceId = useAuthStore((s) => s.user?.workspaceId ?? null);
  const setAuth = useAuthStore((s) => s.setAuth);
  const lastIdentity = useAuthStore((s) => s.lastIdentity);

  const [displayName, setDisplayName] = useState(lastIdentity?.displayName ?? '');
  const [species, setSpecies] = useState<DuckSpecies>(lastIdentity?.species ?? 'duck');
  const [duckColor, setDuckColor] = useState<DuckColor>(lastIdentity?.duckColor ?? 'yellow');

  // Picking an animal snaps the tint to its natural default (still recolourable below). See RegisterPage.
  const onSpeciesChange = (next: DuckSpecies): void => {
    setSpecies(next);
    setDuckColor(SPECIES_META[next].defaultColor);
  };

  const preview = useQuery({
    queryKey: ['invite', key],
    queryFn: () => api.invitePreview(key),
    enabled: key.length > 0,
  });

  const mutation = useMutation({
    mutationFn: api.acceptGuestInvite,
    onSuccess: (result) => {
      setAuth(result);
      // Straight into the invited room via its deep-link (the theme segment self-heals on canonicalize).
      navigate(buildRoomPath('office', result.user.workspaceId), { replace: true });
    },
  });

  // URL is authoritative: only skip the form when you're ALREADY in the guild this invite leads to
  // (then it's just "re-enter your world"). A session in a DIFFERENT guild does NOT short-circuit —
  // the form runs and acceptGuestInvite switches you into the invited world. We wait for the preview
  // so the decision is never made on a stale assumption.
  const alreadyInThisGuild =
    authed && preview.data?.valid === true && preview.data.workspaceId === currentWorkspaceId;
  if (alreadyInThisGuild && currentWorkspaceId) {
    return <Navigate to={buildRoomPath('office', currentWorkspaceId)} replace />;
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    mutation.mutate({ inviteKey: key, displayName, duckColor, species });
  };

  const invalid = preview.isSuccess && !preview.data.valid;
  const subtitle = preview.data?.valid
    ? `Joining ${preview.data.workspaceName} as ${preview.data.role.toLowerCase()}`
    : 'Pick your character and join the room';

  return (
    <AuthShell title="You're invited 🎉" subtitle={subtitle}>
      {invalid ? (
        <div className="text-center">
          <ErrorText>This invite link is invalid, expired, or fully used.</ErrorText>
          <Link to="/register" className="font-medium text-amber-300 hover:text-amber-200">
            Create your own room instead
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit}>
          <ErrorText>{mutation.error ? (mutation.error as ApiError).message : null}</ErrorText>
          {/* M0: the critter you'll be, previewed live — picked instantly above the form, before any
              detail is asked for, so the very first interaction is delightful and reversible. */}
          <CritterPreview species={species} color={duckColor} />
          <Field
            label="Your name"
            required
            autoComplete="nickname"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />

          <SpeciesPicker value={species} onChange={onSpeciesChange} />
          <DuckPicker value={duckColor} onChange={setDuckColor} />

          <SubmitButton loading={mutation.isPending || preview.isLoading}>
            Join the room
          </SubmitButton>
        </form>
      )}
    </AuthShell>
  );
}
